export function computePortfolioTotals(portfolio, options) {
	const { shortingEnabled, includeShortBreakdown } = options;

	if (!shortingEnabled || !includeShortBreakdown) {
		let total = 0;
		let longTotal = 0;
        let longPL = 0;
		for (const asset of portfolio.Assets) {
			// Make sure we only add the game currency to the total
			// We don't want to count it towards long totals!!
			if (asset.IsGameCurrency) {
				total += asset.LongValue || asset.Value || 0;
				continue;
			}
            const longQtyRaw = Number(asset.LongQuantity || 0);
            const longValue = Number(asset.LongValue || asset.Value || 0);
            const longDeltaMoney = longValue - longValue / (1 + (asset.ValueGrowth || 0));

            const priceNow = longQtyRaw ? longValue / longQtyRaw : asset.StockRate;
            const entryPrice = longQtyRaw ? (longValue - longDeltaMoney) / longQtyRaw : asset.StockRate;

			total += longValue;
			longTotal += longValue;
            longPL += (entryPrice && priceNow) ? (priceNow - entryPrice) * longQtyRaw : 0;
		}
		return { total, longTotal, shortTotal: 0, shortPL: 0, longPL };
	}

	let baseTotal = 0;
	let longTotal = 0;
    let shortTotal = 0;
	let shortPL = 0;
    let longPL = 0;

	for (const asset of portfolio.Assets) {
		if (asset.IsGameCurrency) {
			baseTotal += asset.Value || 0;
			continue;
		}

		const longValue = (typeof asset.LongValue === "number" && asset.LongValue > 0)
			? asset.LongValue
			: ((typeof asset.Value === "number" && asset.Value > 0) ? asset.Value : 0);
		if (longValue > 0) {
            const longQtyRaw = Number(asset.LongQuantity || 0);
            const longValue = Number(asset.LongValue || asset.Value || 0);
            const longDeltaMoney = longValue - longValue / (1 + (asset.ValueGrowth || 0));

            const priceNow = longQtyRaw ? longValue / longQtyRaw : asset.StockRate;
            const entryPrice = longQtyRaw ? (longValue - longDeltaMoney) / longQtyRaw : asset.StockRate;

			longTotal += longValue;
            longPL += (entryPrice && priceNow) ? (priceNow - entryPrice) * longQtyRaw : 0;
		}

		const hasShort = asset.HasShortPosition === true
			|| (typeof asset.ShortQuantity === "number" && asset.ShortQuantity > 0)
			|| (typeof asset.ShortValue === "number" && asset.ShortValue < 0);
		if (!hasShort) continue;

		const shortValueAbs = Math.abs(asset.ShortValue || 0);

		const entryValue = (asset.ShortEntryValue !== undefined && asset.ShortEntryValue !== null)
			? asset.ShortEntryValue
			: ((asset.ShortDisplayValue !== undefined && asset.ShortDisplayValue !== null)
				? asset.ShortDisplayValue
				: shortValueAbs);

		const currentValue = (asset.ShortCurrentValue !== undefined && asset.ShortCurrentValue !== null)
			? asset.ShortCurrentValue
			: shortValueAbs;

		const profit = (asset.ShortProfit !== undefined && asset.ShortProfit !== null)
			? asset.ShortProfit
			: (entryValue - currentValue);

        shortTotal += currentValue;
		shortPL += profit;
	}

	const total = baseTotal + longTotal + shortPL;
	return { bank: baseTotal, total, longTotal, shortTotal, shortPL, longPL };
}

export function buildDisplayAssets(asset, options) {
	const { shortingEnabled, includeShortBreakdown } = options;
	const rows = [];

	const shortQty = asset.ShortQuantity || 0;
	const shortValueAbs = Math.abs(asset.ShortValue || 0);
	const hasShortFlag = asset.HasShortPosition === true;
	const baseTodayPct = (asset.Change && asset.Change["1d"]) ? asset.Change["1d"].RelativeChange * 100 : null;

	if (shortingEnabled && includeShortBreakdown && (hasShortFlag || shortQty > 0 || shortValueAbs > 0)) {
		const longQtyRaw = Number(asset.LongQuantity || 0);
		const hasLongQty = longQtyRaw > 0;

		if (hasLongQty) {
			const longValue = Number(asset.LongValue || asset.Value || 0);
			const longDeltaMoney = longValue - longValue / (1 + (asset.ValueGrowth || 0));
			const longDeltaPct = (asset.ValueGrowth || 0) * 100;

			rows.push({
				...asset,
				Type: "long",
				DisplayQuantity: longQtyRaw,
				DisplayValue: longValue,
				totalDeltaMoney: longDeltaMoney,
				totalDeltaPct: longDeltaPct,
				priceNow: longQtyRaw ? longValue / longQtyRaw : asset.StockRate,
				entryPrice: longQtyRaw ? (longValue - longDeltaMoney) / longQtyRaw : asset.StockRate,
				todayChangePct: baseTodayPct,
			});
		}

		const fallbackAbsValue = Math.abs(asset.ShortValue || 0);
		const entryValue = (asset.ShortEntryValue !== undefined && asset.ShortEntryValue !== null)
			? Number(asset.ShortEntryValue)
			: (asset.ShortDisplayValue !== undefined && asset.ShortDisplayValue !== null
				? Number(asset.ShortDisplayValue)
				: fallbackAbsValue);
		const displayValue = (asset.ShortDisplayValue !== undefined && asset.ShortDisplayValue !== null)
			? Number(asset.ShortDisplayValue)
			: entryValue;
		const currentValue = (asset.ShortCurrentValue !== undefined && asset.ShortCurrentValue !== null)
			? Number(asset.ShortCurrentValue)
			: fallbackAbsValue;
		const shortProfit = (asset.ShortProfit !== undefined && asset.ShortProfit !== null)
			? Number(asset.ShortProfit)
			: (entryValue - currentValue);
		const shortPercent = entryValue !== 0 ? (shortProfit / entryValue) * 100 : 0;
		const displayQuantity = shortQty > 0 ? shortQty : Math.max(-(Number(asset.Quantity || 0)), 0);
		const priceNow = displayQuantity ? currentValue / displayQuantity : asset.StockRate;
		const entryPrice = displayQuantity ? entryValue / displayQuantity : asset.StockRate;

		rows.push({
			...asset,
			Type: "short",
			DisplayQuantity: displayQuantity,
			DisplayValue: displayValue,
			ShortCurrentValue: currentValue,
			ShortEntryValue: entryValue,
			ShortDisplayValue: displayValue,
			ShortProfit: shortProfit,
			ShortPercent: shortPercent,
			totalDeltaMoney: shortProfit,
			totalDeltaPct: shortPercent,
			priceNow,
			entryPrice,
			todayChangePct: baseTodayPct !== null ? -baseTodayPct : null,
		});

		return rows;
	}

	const quantity = Number(
		asset.DisplayQuantity !== undefined && asset.DisplayQuantity !== null
			? asset.DisplayQuantity
			: (asset.LongQuantity !== undefined && asset.LongQuantity !== null
				? asset.LongQuantity
				: (asset.Quantity !== undefined && asset.Quantity !== null ? asset.Quantity : 0))
	);
	const value = Number(
		asset.DisplayValue !== undefined && asset.DisplayValue !== null
			? asset.DisplayValue
			: (asset.LongValue !== undefined && asset.LongValue !== null
				? asset.LongValue
				: (asset.Value !== undefined && asset.Value !== null ? asset.Value : 0))
	);
	const baseDeltaMoney = value - value / (1 + (asset.ValueGrowth || 0));
	const baseDeltaPct = (asset.ValueGrowth || 0) * 100;
	const isShortRow = hasShortFlag;
	const treatShortAsLong = isShortRow && !(shortingEnabled && includeShortBreakdown);
	const adjustedDeltaMoney = (isShortRow && !treatShortAsLong) ? -baseDeltaMoney : baseDeltaMoney;
	const adjustedDeltaPct = (isShortRow && !treatShortAsLong) ? -baseDeltaPct : baseDeltaPct;
	const adjustedTodayPct = (baseTodayPct !== null && isShortRow && !treatShortAsLong)
		? -baseTodayPct
		: baseTodayPct;
	const displayType = (isShortRow && !treatShortAsLong) ? "short" : "long";

	const row = {
		...asset,
		Type: displayType,
		totalDeltaPct: adjustedDeltaPct,
		todayChangePct: adjustedTodayPct,
	};

	if (!treatShortAsLong) {
		if (quantity) {
			row.DisplayQuantity = quantity;
		}
		if (value) {
			row.DisplayValue = value;
		}
		if (!Number.isNaN(adjustedDeltaMoney) && adjustedDeltaMoney !== undefined && adjustedDeltaMoney !== null) {
			row.totalDeltaMoney = adjustedDeltaMoney;
		}
		if (quantity) {
			row.priceNow = value / quantity;
			row.entryPrice = (value - baseDeltaMoney) / quantity;
		}
	} else {
		row.DisplayQuantity = undefined;
		row.DisplayValue = undefined;
		row.Quantity = null;
		row.Value = null;
		row.totalDeltaMoney = undefined;
		row.priceNow = undefined;
		row.entryPrice = undefined;
	}

	rows.push(row);

	return rows;
}



// WEBPACK FOOTER //
// ./src/PortfolioHelpers.js